/**
 * Featured Cards Carousel component using BlazeSlider.
 *
 * Example usage:
 *  <featured-cards-carousel
 *    data-slides-per-view="1.2,2.5,4"
 *    data-slide-gap="20,20,40"
 *    data-loop="false"
 *    data-draggable="true"
 *  >
 *    <div class="blaze-slider">
 *      <div class="blaze-container">
 *        <div class="blaze-track-container">
 *          <div class="blaze-track">
 *            <div class="blaze-slide">Slide 1</div>
 *            <div class="blaze-slide">Slide 2</div>
 *          </div>
 *        </div>
 *      </div>
 *    </div>
 *  </featured-cards-carousel>
 *
 * Data attributes:
 * - data-slides-per-view: Comma-separated values for mobile, tablet (750px+), desktop (990px+). Default: "1,1,1"
 * - data-slide-gap: Comma-separated gap values in px for mobile, tablet, desktop. Default: "0,0,0"
 * - data-loop: Enable infinite loop. Default: "true"
 * - data-draggable: Enable drag/swipe. Default: "true"
 * - data-custom-previous: Custom selector for prev button
 * - data-custom-next: Custom selector for next button
 * - data-transition-duration: Transition duration in ms. Default: "300"
 */

if (!customElements.get("featured-cards-carousel")) {
  customElements.define(
    "featured-cards-carousel",
    class FeaturedCardsCarousel extends HTMLElement {
      constructor() {
        super();
        this.initializationAttempts = 0;
        this.maxAttempts = 10;
        this.blazeSlider = null;
      }

      connectedCallback() {
        this.initializeBlazeSlider();
      }

      disconnectedCallback() {
        if (this.blazeSlider) {
          this.blazeSlider.destroy();
        }
      }

      parseCommaSeparatedValues(value, defaultValue = "1,1,1") {
        const values = (value || defaultValue).split(",").map((v) => v.trim());
        return {
          mobile: parseFloat(values[0]) || 1,
          tablet: parseFloat(values[1]) || parseFloat(values[0]) || 1,
          desktop:
            parseFloat(values[2]) ||
            parseFloat(values[1]) ||
            parseFloat(values[0]) ||
            1,
        };
      }

      initializeBlazeSlider() {
        const sliderElement = this.querySelector(".blaze-slider");
        if (!sliderElement) {
          console.warn(
            "featured-cards-carousel: No .blaze-slider element found"
          );
          return;
        }

        const slides = sliderElement.querySelectorAll(".blaze-slide");

        // If only one slide, no need for slider functionality
        if (slides.length <= 1) {
          return;
        }

        // Check if BlazeSlider is available
        if (typeof window.BlazeSlider === "undefined") {
          this.initializationAttempts++;

          if (this.initializationAttempts < this.maxAttempts) {
            setTimeout(() => this.initializeBlazeSlider(), 200);
          } else {
            console.error(
              "featured-cards-carousel: BlazeSlider failed to load after",
              this.maxAttempts,
              "attempts"
            );
          }
          return;
        }

        // Parse configuration from data attributes
        const slidesPerView = this.parseCommaSeparatedValues(
          this.dataset.slidesPerView,
          "1,1,1"
        );
        const slideGap = this.parseCommaSeparatedValues(
          this.dataset.slideGap,
          "0,0,0"
        );
        const loop = this.dataset.loop !== "false";
        const draggable = this.dataset.draggable !== "false";
        const transitionDuration =
          parseInt(this.dataset.transitionDuration) || 300;

        try {
          // Initialize Blaze Slider
          // Note: Blaze Slider uses desktop-first approach, so 'all' should have desktop values
          const blazeSlider = new window.BlazeSlider(sliderElement, {
            all: {
              enableAutoplay: false,
              enablePagination: false,
              slidesToShow: slidesPerView.desktop,
              slidesToScroll: 1,
              transitionDuration: transitionDuration,
              transitionTimingFunction: "ease-in-out",
              loop: loop,
              draggable: draggable,
              slideGap: `${slideGap.desktop}px`,
            },
            "(max-width: 749px)": {
              slidesToShow: slidesPerView.mobile,
              slideGap: `${slideGap.mobile}px`,
            },
            "(min-width: 750px) and (max-width: 989px)": {
              slidesToShow: slidesPerView.tablet,
              slideGap: `${slideGap.tablet}px`,
            },
          });

          this.blazeSlider = blazeSlider;

          // Set up navigation with state management
          this.setupNavigation();

          // Track current page index internally
          this.currentPageIndex = 0;
          this.totalPages = Math.ceil(
            blazeSlider.totalSlides - blazeSlider.config.slidesToShow + 1
          );

          // Handle slide change events
          blazeSlider.onSlide((pageIndex, firstSlideIndex, lastSlideIndex) => {
            this.currentPageIndex = pageIndex;
            this.updateNavigationState();
          });

          // Initial state
          this.updateNavigationState();
        } catch (error) {
          console.error(
            "featured-cards-carousel: Error initializing BlazeSlider:",
            error
          );
        }
      }

      setupNavigation() {
        // Find navigation buttons - check for custom selectors first
        const prevSelector = this.dataset.customPrevious;
        const nextSelector = this.dataset.customNext;

        this.prevButton = prevSelector
          ? document.querySelector(prevSelector)
          : this.querySelector(".blaze-nav-prev");

        this.nextButton = nextSelector
          ? document.querySelector(nextSelector)
          : this.querySelector(".blaze-nav-next");

        if (this.prevButton) {
          this.prevButton.addEventListener("click", () => {
            if (this.blazeSlider) {
              this.blazeSlider.prev();
              this.currentPageIndex = Math.max(0, this.currentPageIndex - 1);
              this.updateNavigationState();
            }
          });
        }

        if (this.nextButton) {
          this.nextButton.addEventListener("click", () => {
            if (this.blazeSlider) {
              this.blazeSlider.next();
              this.currentPageIndex = Math.min(
                this.totalPages - 1,
                this.currentPageIndex + 1
              );
              this.updateNavigationState();
            }
          });
        }
      }

      updateNavigationState() {
        if (!this.blazeSlider || !this.prevButton || !this.nextButton) return;

        // If loop is enabled, buttons are always enabled
        if (this.dataset.loop !== "false") {
          this.prevButton.classList.remove("blaze-button-disabled");
          this.nextButton.classList.remove("blaze-button-disabled");
          this.prevButton.removeAttribute("aria-disabled");
          this.nextButton.removeAttribute("aria-disabled");
          return;
        }

        // Disable prev button if at the start
        const atStart = this.currentPageIndex === 0;
        if (atStart) {
          this.prevButton.classList.add("blaze-button-disabled");
          this.prevButton.setAttribute("aria-disabled", "true");
        } else {
          this.prevButton.classList.remove("blaze-button-disabled");
          this.prevButton.removeAttribute("aria-disabled");
        }

        // Disable next button if at the end
        const atEnd = this.currentPageIndex >= this.totalPages - 1;
        if (atEnd) {
          this.nextButton.classList.add("blaze-button-disabled");
          this.nextButton.setAttribute("aria-disabled", "true");
        } else {
          this.nextButton.classList.remove("blaze-button-disabled");
          this.nextButton.removeAttribute("aria-disabled");
        }
      }
    }
  );
}
